/**
 * Fonte única dos breakpoints do front-end web (Vue 3). CSS custom property não funciona dentro de
 * `@media`, então a família de larguras vive aqui em TypeScript e é **espelhada** em
 * `tokens/breakpoints.css` (`--dt-bp-*`), que o teste `breakpoints-css-sync` obriga a não divergir.
 *
 * Regra de plataforma (ver `.github/instructions/web-accessibility-ux.instructions.md`):
 * **Vue atende navegadores de celular, tablet e desktop; Flutter atende instalação nativa
 * Android/iOS.** Portanto todo número aqui descreve largura de *viewport de navegador*, não
 * classe de dispositivo.
 *
 * Nenhum SFC deve conter número de breakpoint literal: em TypeScript use `BREAKPOINTS`/
 * `SHELL_BREAKPOINTS`; em CSS use as media queries documentadas abaixo, com o comentário do nome
 * do breakpoint ao lado (é a única duplicação inevitável, e o teste de sincronia a cobre).
 */

/**
 * Larguras fracionárias existem (zoom do navegador, DPR, texto ampliado). Um limite "abaixo de N"
 * em CSS é escrito `N - 0.02` — `999px` deixaria de casar em `999.5px`.
 */
export const SUB_PIXEL_STEP = 0.02;

export const BREAKPOINTS = {
  /** Menor largura suportada sem perda de função (critério de aceite, não ponto de troca). */
  min: 320,
  /** Família comum aos 3 apps (fonte: breakpoints.dart). */
  mobile: 600,
  tablet: 900,
  desktop: 1200,
  wide: 1440,
  /** Troca rail ↔ bottom nav do shell do Operacional (op/styles.css, `useMobile()`). */
  opShellSwitch: 1000,
  /** Rail do Operacional em modo compacto (214px em vez de 248px). */
  opRailCompact: 1180,
  /** Grade do Operacional empilha em coluna única. */
  opStackedGrid: 560,
  /** Troca rail ↔ bottom nav do shell da Clínica (Docs/V2/Clinica/clinica/app.jsx: `app.m`/`app.d`). */
  clinicShellSwitch: 1000,
  /** Troca rail ↔ bottom nav do shell do Técnico (valor Dart `breakpoints.dart`). */
  techShellSwitch: 600,
  /** `DtDataTable`/`DtSkeletonTable`: `<table>` ↔ cartões. Ver `SHELL_BREAKPOINTS` para o porquê. */
  tableCardSwitch: 1000,

  /*
   * Breakpoints de CONTEÚDO do Operacional. Não trocam o shell — reorganizam a grade interna de
   * uma tela (filtros, painéis lado a lado, cartões de KPI). Estão aqui, e não soltos no SFC,
   * porque cada número passou a valer para várias telas e precisa de um nome único.
   */
  /** Filtros e cabeçalhos de tela empilham em coluna única (Dashboard, Faturas). */
  opContentStack: 420,
  /** Painel de duas colunas vira coluna única (Fluxo de caixa, Pagamentos, Manutenções lançadas). */
  opContentCompact: 680,
  /** Grade padrão das telas de fila/lista empilha (o mais usado do Operacional). */
  opContentSplit: 720,
  /** Painéis largos (Check-up, Repasses, Textos) passam a coluna única. */
  opContentWide: 820,
  /** Coluna auxiliar de Técnicos aparece só a partir daqui. */
  opContentDesk: 980,
} as const;

export type BreakpointName = keyof typeof BREAKPOINTS;

/**
 * Como o limite se comporta exatamente **no** valor:
 * - `below`  → mobile em `< px`   → CSS `(max-width: {px - 0.02}px)`
 * - `upTo`   → mobile em `<= px`  → CSS `(max-width: {px}px)`
 */
export type BreakpointBound = 'below' | 'upTo';

/** String de media query correspondente ao par (px, limite). Use sempre isto, nunca concatenação local. */
export function maxWidthQuery(px: number, bound: BreakpointBound = 'upTo'): string {
  const limit = bound === 'below' ? px - SUB_PIXEL_STEP : px;
  return `(max-width: ${String(limit)}px)`;
}

/** String de media query para o lado desktop do mesmo limite — complemento exato de `maxWidthQuery`. */
export function minWidthQuery(px: number, bound: BreakpointBound = 'upTo'): string {
  const limit = bound === 'below' ? px : px + SUB_PIXEL_STEP;
  return `(min-width: ${String(limit)}px)`;
}

export interface ShellBreakpoint {
  /** Largura nominal do limite, em CSS px. */
  readonly px: number;
  /** Se o próprio `px` já é mobile (`upTo`) ou ainda é desktop (`below`). */
  readonly bound: BreakpointBound;
  /** Media query exata a usar em `matchMedia` **e** em `@media` — os dois lados nunca divergem. */
  readonly query: string;
  /** Custom property espelhada em `tokens/breakpoints.css`. */
  readonly cssVar: `--dt-bp-${string}`;
}

function shellBreakpoint(px: number, bound: BreakpointBound, cssVar: `--dt-bp-${string}`): ShellBreakpoint {
  return { px, bound, query: maxWidthQuery(px, bound), cssVar };
}

/**
 * Limite de troca rail ↔ bottom nav por shell. **Os números não são intercambiáveis entre apps** —
 * cada um nasceu de uma fonte diferente e continua com o limite que já estava publicado:
 *
 * | Shell        | Mobile quando | Media query          | Origem                                   |
 * |--------------|---------------|----------------------|------------------------------------------|
 * | Clínica      | `< 1000px`    | `max-width:999.98px` | protótipo `clinica/app.jsx` (`app.m/.d`)  |
 * | Técnico      | `<= 600px`    | `max-width:600px`    | `breakpoints.dart` (sem protótipo web)    |
 * | Operacional  | `<= 1000px`   | `max-width:1000px`   | protótipo `op/styles.css` (`useMobile()`) |
 *
 * A Clínica é a única com limite exclusivo (`below`): no protótipo o desktop começa **em** 1000px.
 */
export const SHELL_BREAKPOINTS = {
  clinica: shellBreakpoint(BREAKPOINTS.clinicShellSwitch, 'below', '--dt-bp-clinic-shell-switch'),
  tecnico: shellBreakpoint(BREAKPOINTS.techShellSwitch, 'upTo', '--dt-bp-tech-shell-switch'),
  operacional: shellBreakpoint(BREAKPOINTS.opShellSwitch, 'upTo', '--dt-bp-op-shell-switch'),
} as const;

export type ShellName = keyof typeof SHELL_BREAKPOINTS;

/**
 * Limite de troca `<table>` ↔ cartões do `DtDataTable`/`DtSkeletonTable`. É um breakpoint do
 * **componente**, não do shell: uma tabela de 6+ colunas não cabe num rail-menos-conteúdo abaixo
 * de 1000px em nenhum dos apps. Quem precisar de outro limite passa a prop `mobileMaxWidthPx`.
 */
export const TABLE_CARD_BREAKPOINT: ShellBreakpoint = shellBreakpoint(
  BREAKPOINTS.tableCardSwitch,
  'upTo',
  '--dt-bp-table-card-switch',
);
